// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { ArchiveControl } from '@/components/archive-control';
import {
  EditRounded,
  MergeRounded,
  VisibilityOffOutlined,
} from '@/constants/hardfin-standard-icons';
import { useSort } from '@/hooks/use-sort';
import { useTableSort } from '@/hooks/use-table-sort';
import { SparseLocation } from '@/sparse/api/types';
import {
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo } from 'react';

type ZoneSortProperty = 'name';

interface Props {
  zones: SparseLocation[];
  onEditZone: (zone: SparseLocation) => void;
  onMergeZone: (zone: SparseLocation) => void;
  onArchiveZone: (zone: SparseLocation, isArchived: boolean) => void;
}

export function ZonesTable(props: Props) {
  const { sort, setSort } = useSort<ZoneSortProperty>('name');
  const { getSortDirection, getLabelDirection, isLabelActive } =
    useTableSort(sort);

  const sortedZones = useMemo(() => {
    // Default zone (SITE type) always stays first
    const defaultZone = props.zones.filter((z) => z.type === 'SITE');
    const regularZones = [...props.zones.filter((z) => z.type === 'ZONE')];

    regularZones.sort((a, b) => {
      const cmp = a.name.localeCompare(b.name);

      return sort.direction === 'asc' ? cmp : -cmp;
    });

    return [...defaultZone, ...regularZones];
  }, [props.zones, sort]);

  if (props.zones.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        There are no zones at this location
      </Typography>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sortDirection={getSortDirection('name')}>
              <TableSortLabel
                active={isLabelActive('name')}
                direction={getLabelDirection('name')}
                onClick={() => setSort('name')}
              >
                Name
              </TableSortLabel>
            </TableCell>
            <TableCell>Inventory</TableCell>
            <TableCell>Consignee</TableCell>
            <TableCell>Description</TableCell>
            <TableCell align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedZones.map((zone) => {
            const textColor = zone.isArchived
              ? 'text.disabled'
              : 'text.primary';

            const inheritedColor = zone.isArchived
              ? 'text.disabled'
              : 'text.secondary';

            return (
              <TableRow key={zone.id}>
                <TableCell>
                  <Stack spacing={1} direction="row" alignItems="center">
                    <Typography variant="body2" color={textColor}>
                      {zone.name}
                    </Typography>
                    {zone.isArchived && (
                      <Tooltip title="This zone is archived" arrow>
                        <VisibilityOffOutlined
                          fontSize="small"
                          color="disabled"
                        />
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    color={textColor}
                    component="span"
                  >
                    {zone.isInventory ? 'Yes' : 'No'}
                  </Typography>
                  {zone.type === 'ZONE' && !zone.isInventoryOverride && (
                    <Typography
                      component="span"
                      variant="body2"
                      color={inheritedColor}
                    >
                      {' '}
                      (inherited)
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color={textColor}>
                    {zone.consignee?.name ?? '\u2014'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color={textColor}>
                    {zone.description ?? '\u2014'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {zone.type === 'ZONE' && (
                    <Stack
                      direction="row"
                      spacing={0.5}
                      justifyContent="flex-end"
                    >
                      <ArchiveControl
                        isArchived={zone.isArchived}
                        onChange={(isArchived) =>
                          props.onArchiveZone(zone, isArchived)
                        }
                      />
                      {!zone.isArchived && (
                        <>
                          <Tooltip arrow title="Merge this zone with another">
                            <IconButton
                              size="small"
                              onClick={() => props.onMergeZone(zone)}
                            >
                              <MergeRounded fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <IconButton
                            size="small"
                            onClick={() => props.onEditZone(zone)}
                          >
                            <EditRounded fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </Stack>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
